import {Accordion, AccordionItem, Avatar, Card, CardBody, CardHeader, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger, SelectItem} from "@heroui/react";
import {Spinner} from "@heroui/spinner";
import {TableBody, TableCell, TableColumn, TableHeader, TableRow} from "@heroui/table";
import {Icon} from "@iconify/react";
import {keepPreviousData, useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute, SearchSchemaInput, useParams, useSearch} from '@tanstack/react-router'
import {useWindowSize} from "@uidotdev/usehooks";
import React, {useState} from "react";
import img_female from "../../../assets/img_female.png";
import img_male from "../../../assets/img_male.png";
import Content from "../../../components/Content.tsx";
import {icons} from "../../../components/icons.tsx";
import PButton from "../../../components/PButton.tsx";
import PInput from "../../../components/PInput.tsx";
import PSelect from "../../../components/PSelect.tsx";
import PTable from "../../../components/PTable.tsx";
import Header from "../../../Header.tsx";
import services from "../../../services";
import {tableFullScreen} from "../../../components/tableStyles.ts";
import queryKeys from "../../../utilities/queryKeys.ts";

export const Route = createFileRoute('/_app/running/leaderboards-results/$ranking_group_uid')({
    validateSearch: (search: Record<string, unknown> & SearchSchemaInput) => ({
        embed: search.embed as string | undefined,
    }),
    component: RouteComponent,
})

function RouteComponent() {
    const params = useParams({strict: false});
    const search = useSearch({strict: false});
    const queryClient = useQueryClient()

    const [filterText, setFilterText] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [raceEntriesFilter, setRaceEntriesFilter] = useState('');
    const [genderFilter, setGenderFilter] = useState('');
    const [sortBy, setSortBy] = useState('');

    const runningLeaderboardResultsQuery = useQuery({
        queryKey:        queryKeys.running.getLeaderboardResults({uid: params.ranking_group_uid, sortBy, categoryFilter, genderFilter}),
        queryFn:         async () => services.running.getLeaderboardResults(params.ranking_group_uid!, {sort_by: sortBy, category: categoryFilter, gender: genderFilter}),
        enabled:         !!params.ranking_group_uid,
        placeholderData: keepPreviousData,
    })

    const size = useWindowSize();
    const isSm = !!size.width && size.width < 1024;

    const columns = (size.width && size.width < 1024
            ? [
                {key: 'combined', title: 'Runner'},
            ]
            : [
                // ...(categoryFilter
                //         ? [{key: 'category_final_position', title: 'Category Position'}]
                //         : [{key: 'cumulativeposition', title: 'Cumulative Position'}]
                // ),
                {key: '_cumulative_position', title: 'Overall Position'},
                {key: '_cumulative_points', title: 'Overall Points'},
                {key: '_category_final_position', title: 'Category Position'},
                {key: '_cumulative_category_points', title: 'Category Points'},
                {key: 'name', title: 'Name'},
                {key: '_entries_count', title: 'Race Entries'},
                {key: 'countrycode', title: 'Country'},
                {key: 'gender', title: 'Gender'},
                {key: 'category', title: 'Category'},
            ]
    );

    const isEmbed = (search.embed === 'true');

    if (runningLeaderboardResultsQuery.isPending) {
        return <>
            {!isEmbed && <Header />}

            <Content className="flex justify-center my-6">
                <Spinner size={"lg"} />
            </Content>
        </>
    }

    const runningLeaderboardResultsQueryResults = (runningLeaderboardResultsQuery.data?.results || [])
        .filter((r) => !filterText || r.name.toLowerCase().includes(filterText.toLowerCase()))
        // .filter((r) => !categoryFilter || r.category === categoryFilter)
        .filter((r) => !raceEntriesFilter || +r._entries_count === +raceEntriesFilter);

    return (
        <>
            {!isEmbed && <Header backUrl={"/running"} />}
            <Content>

                <div className="w-full flex justify-between items-start max-sm:items-start mb-4">
                    <div className={"flex max-sm:flex-col"}>
                        <Accordion defaultExpandedKeys={isSm ? undefined : ['filters']}>
                            <AccordionItem key="filters" aria-label="Filters" title="Filters" classNames={{content: "flex items-end max-sm:flex-col max-sm:items-start gap-1", heading: (isSm ? '' : "hidden")}}>
                                <PInput
                                    label={"Name:"}
                                    labelPlacement={"outside-top"}
                                    className={"w-auto min-w-20"}
                                    onBlur={(e) => setFilterText(e.target.value)}
                                />
                                <PSelect
                                    placeholder={"category"}
                                    defaultSelectedKeys={[categoryFilter]}
                                    onSelectionChange={(e) => {
                                        setCategoryFilter(e.currentKey as any)
                                    }}
                                    label={"Category:"}
                                    multiple={true}
                                    disallowEmptySelection={true}
                                >
                                    <SelectItem key={""}></SelectItem>
                                    {(runningLeaderboardResultsQuery.data?.categories || []).map((category: any) => <SelectItem key={category}>{category}</SelectItem>)}
                                </PSelect>
                                <PSelect
                                    placeholder={"entries"}
                                    defaultSelectedKeys={[raceEntriesFilter]}
                                    onSelectionChange={(e) => {
                                        setRaceEntriesFilter(e.currentKey as any)
                                    }}
                                    label={"Race entries:"}
                                    multiple={true}
                                    disallowEmptySelection={true}
                                    className={"min-w-24"}
                                >
                                    <SelectItem key={""}></SelectItem>
                                    {(runningLeaderboardResultsQuery.data?.raceEntries || []).map((raceEntry: any) => <SelectItem key={String(raceEntry)}>{String(raceEntry)}</SelectItem>)}
                                </PSelect>
                                <PSelect
                                    defaultSelectedKeys={[raceEntriesFilter]}
                                    onSelectionChange={(e) => {
                                        setGenderFilter(e.currentKey as any)
                                    }}
                                    label={"Gender:"}
                                    multiple={true}
                                    disallowEmptySelection={true}
                                    className={"min-w-24"}
                                >
                                    <SelectItem key={""}></SelectItem>
                                    <SelectItem key={"M"}>Male</SelectItem>
                                    <SelectItem key={"F"}>Female</SelectItem>
                                </PSelect>
                                <PButton className={"ml-2 max-sm:mt-2"}>Search</PButton>
                            </AccordionItem>
                        </Accordion>
                    </div>

                    <div>
                        <PButton color="default" onPress={() => queryClient.invalidateQueries({queryKey: queryKeys.running.getLeaderboardResults()})}
                                 className={"ml-2"}
                                 startContent={<Icon icon="material-symbols-light:refresh" className={"text-2xl " + (runningLeaderboardResultsQuery.isFetching ? 'animate-spin' : '')} />}
                                 isIconOnly={isSm}>
                            {isSm ? '' : 'refresh'}
                        </PButton>

                        <Dropdown placement="bottom-end">
                            <DropdownTrigger>
                                <PButton className="flex-none max-sm:mt-2 ml-2 light:bg-[#E6E8F3] dark:border dark:border-primary" color="primary" variant="light" isIconOnly={true}>
                                    {icons.settings()}
                                </PButton>
                            </DropdownTrigger>
                            <DropdownMenu
                                disallowEmptySelection
                                hideSelectedIcon={true}
                                aria-label="Options"
                                className="max-w-[300px]"
                                selectionMode="single"
                            >
                                {runningLeaderboardResultsQuery.data?.sortedBy !== 'overall' ?
                                    <DropdownItem key="overall" onPress={() => setSortBy('overall')}>
                                        Sort by: Overall position
                                    </DropdownItem>
                                    : <></>}
                                {runningLeaderboardResultsQuery.data?.sortedBy !== 'category' ?
                                    <DropdownItem key="category" onPress={() => setSortBy('category')}>
                                        Sort by: Category position
                                    </DropdownItem>
                                    : <></>}
                            </DropdownMenu>
                        </Dropdown>
                    </div>
                </div>
                <Card>
                    <CardHeader className={"flex items-center justify-between"}>
                        <div className={"font-bold text-lg"}>{runningLeaderboardResultsQuery.data?.name} Race Results</div>
                        <div>
                            {runningLeaderboardResultsQueryResults?.length} runners
                        </div>
                    </CardHeader>
                    <CardBody>
                        <PTable removeWrapper={true} classNames={tableFullScreen} isStriped={true} aria-label={"Running Results"}>
                            <TableHeader columns={columns}>
                                {(column) => <TableColumn key={column.key}>
                                    {column.title}
                                </TableColumn>}
                            </TableHeader>
                            <TableBody>
                                {runningLeaderboardResultsQueryResults.map((result) => <TableRow key={result.uid}>
                                    {(columnKey) => {
                                        if (columnKey === 'combined') {
                                            return <TableCell>
                                                <div className="flex w-full">
                                                    <div className={"flex items-center mr-2"}>
                                                        {result._cumulative_position}.
                                                    </div>
                                                    <div>
                                                        <Avatar className={"mr-2"} src={result.profile_thumb_url || result.profile_image_url || (result.gender === "M" ? img_male : img_female)} />
                                                    </div>
                                                    <div>
                                                        <div className="flex-1 flex items-center">{result.name} {result.lastname}</div>
                                                        <div>
                                                            <small>
                                                                <img className="inline" src={'https://flagcdn.com/16x12/' + result.countrycode?.toLowerCase() + '.png'} title={result.countrycode} alt={result.countrycode + " flag"} />
                                                                {' '}
                                                                {result.gender === 'M' ? 'Male' : 'Female'}, {result._entries_count} entries, {result._cumulative_points} overall pts
                                                            </small>
                                                        </div>
                                                        <div>
                                                            <small>Category: {result.category} (#{result._category_final_position} with {result._cumulative_category_points} pts)</small>
                                                        </div>
                                                    </div>
                                                </div>
                                            </TableCell>
                                        }
                                        if (columnKey === 'name') {
                                            return <TableCell>
                                                <div className={"flex w-full items-center"}>
                                                    <Avatar className={"mr-2"} src={result.profile_thumb_url || result.profile_image_url || img_male} />
                                                    {result.name} {result.lastname}
                                                </div>
                                            </TableCell>
                                        }
                                        if (columnKey === 'gender') {
                                            return <TableCell>
                                                {result.gender === 'M' ? 'Male' : 'Female'}
                                            </TableCell>
                                        }
                                        if (columnKey === 'countrycode') {
                                            return <TableCell>
                                                <img className="inline" src={'https://flagcdn.com/16x12/' + result.countrycode?.toLowerCase() + '.png'} title={result.countrycode} alt={result.countrycode + " flag"} />
                                            </TableCell>
                                        }
                                        if (result.hasOwnProperty(columnKey)) {
                                            return <TableCell>
                                                {result[columnKey] || '-'}
                                            </TableCell>
                                        }
                                        return (
                                            <TableCell>
                                                {columnKey}
                                            </TableCell>
                                        );
                                    }}
                                </TableRow>)}
                            </TableBody>

                        </PTable>
                    </CardBody>
                </Card>
            </Content>
        </>
    );
}
